import styled, { keyframes, css } from "styled-components"
// -------------------------------------
// Animations
// -------------------------------------
export const slideDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

// -------------------------------------
// Styled Components
// -------------------------------------

interface HeaderLayoutProps {
  mode: "transparent" | "glassy" | "solid"
}

// Outer header container
export const HeaderLayout = styled.header<HeaderLayoutProps>`
  position: absolute; // Start as absolute for transparent mode
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  min-height: 13vh;
  padding: 0 50px;
  padding: 0 4.3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition:
    background-color 0.3s ease,
    box-shadow 0.3s ease,
    backdrop-filter 0.3s ease; // Added backdrop-filter transition;

  @media (max-width: 768px) {
    padding: 0px 30px;
  }

  ${({ mode, theme }) => {
    switch (mode) {
      case "transparent":
        return css`
          background-color: transparent;
          margin-top: 20px;
          position: absolute; // Positioned over the hero
          color: white; // Example: White text for transparent header

          padding-top: 4rem;

          ${NavLink}, ${PhoneLink} {
            // Target NavLink and PhoneLink specifically
            color: white;
            &:hover,
            &:focus {
              color: white; // Keep white or slightly different on hover
              text-shadow: 0 0 5px rgba(255, 255, 255, 0.7); // Optional glow
            }
          }
          ${Logo} svg path {
            // Make SVG logo white
            fill: white;
          }
          ${HamburgerBar} {
            // Make hamburger bars white
            background-color: white;
          }
        `
      case "glassy":
        return css`
          background-color: rgba(255, 255, 255, 0.1); // Semi-transparent white
          backdrop-filter: blur(10px); // Apply blur effect
          box-shadow: 0 2px 10px rgba(146, 146, 146, 0.1);
          position: fixed; // Fixed position when scrolled or on other pages
          /* color: ${theme.textPrimary}; // Default text color */
          color: ${({ theme }) => theme.primary};
          ${NavLink}, ${PhoneLink} {
            color: ${theme.textPrimary};
            &:hover,
            &:focus {
              color: ${theme.primary};
            }
          }
          ${Logo} svg path {
            fill: ${theme.textPrimary};
          }
          ${HamburgerBar} {
            background-color: ${theme.textPrimary};
          }

          button {
            color: ${theme.textPrimary};
            border: 1px solid ${theme.textPrimary};
          }
        `
      case "solid": // Fallback or default solid style
      default:
        return css`
          background-color: ${theme.white}; // Default solid background
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
          position: fixed;
          color: ${theme.primaryText};
          ${NavLink}, ${PhoneLink} {
            color: ${theme.primaryText};
            &:hover,
            &:focus {
              color: ${theme.primary};
            }
          }
          ${Logo} svg path {
            fill: ${theme.primary};
          }
          ${HamburgerBar} {
            background-color: ${theme.primaryText};
          }

          button {
            color: ${theme.textPrimary};
            /* border: 1px solid ${theme.textPrimary}; */
            border: none;
          }
        `
    }
  }}
`

// Logo container
export const Logo = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  /* padding: 0 20px; */

  /* If you have a primary color in your theme, you could use it here. */
  color: ${({ theme }) => theme?.primary || "#000"};

  svg {
    max-width: 220px;
    width: 100%;
    height: auto;
    transition: transform 0.3s ease;
  }

  &:hover,
  &:focus {
    /* Slight enlarge on hover/focus, optional */
    svg {
      /* transform: scale(1.03); */
    }
  }

  @media (max-width: 992px) {
    padding: 0;

    svg {
      max-width: 200px;
    }
  }
`

// Standard desktop nav container
export const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 992px) {
    display: none;
  }
`

// Individual link styling
export const NavLink = styled.a`
  font-size: 1rem;
  font-weight: 400;
  text-decoration: none;
  color: ${({ theme }) => theme?.textPrimary || "#333"};
  transition: color 0.2s ease;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme?.primary || "#ff5349"};
    outline: none;
  }
`

// A phone number link to keep consistent styling with NavLink
export const PhoneLink = styled.a`
  font-size: 1rem;
  font-weight: 400;
  text-decoration: none;
  margin-right: 1.5rem;
  color: ${({ theme }) => theme?.textPrimary || "#333"};
  transition: color 0.2s ease;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme?.primary || "#ff5349"};
    outline: none;
  }
`

// Large call-to-action button on the right
// using a slight gradient to replicate or improve the screenshot style
export const CTAButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  color: #fff;

  /* Gradient or solid color for a modern look */
  background: linear-gradient(135deg, #ff5f6d 0%, #ffc371 100%);
  border-radius: 0.8rem;
  /* For a simpler single color, you could use theme.primary or #D50000, etc. */
  /* background-color: ${({ theme }) => theme?.primary || "#D50000"}; */

  padding: 0.5rem 20px;

  transition: background 0.3s ease;

  &:hover,
  &:focus {
    background: linear-gradient(135deg, #ff5f6d 10%, #ff9966 90%);
    outline: none;
  }

  @media (max-width: 992px) {
    display: none;
  }
`

// Hamburger icon for mobile
export const Hamburger = styled.button`
  display: none;

  @media (max-width: 992px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0 20px;

    outline: none;

    &:focus {
      /* outline: 2px solid ${({ theme }) => theme?.primary || "#D50000"}; */
      /* outline-offset: 4px; */
    }
  }
`

// The three lines inside the hamburger
export const HamburgerBar = styled.div<{ open: boolean; index: number }>`
  width: 25px;
  height: 3px;
  background-color: ${({ theme }) => theme?.textPrimary || "#333"};
  transition:
    transform 0.4s ease,
    opacity 0.4s ease;

  /* Rotate or hide bars based on open state */
  ${({ open, index }) =>
    open && index === 0
      ? "transform: rotate(45deg) translate(5px, 6px);"
      : open && index === 1
        ? "opacity: 0;"
        : open && index === 2
          ? "transform: rotate(-45deg) translate(5px, -6px);"
          : ""}
`

// Mobile menu container
export const MobileMenu = styled.div<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 13vh; // Position below the header height
  left: 0;
  right: 0;
  /* background-color: ${({ theme }) => theme.backgroundPrimary || "#fff"}; */
  background-color: ${({ theme }) => theme.primary || "#fff"};
  /* max-width: 90vw; */

  background-color: rgba(255, 255, 255, 0.9); // Semi-transparent white
  /* margin: 0 1rem; */

  backdrop-filter: blur(10px);
  border-radius: 0 0 0.8rem 0.8rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 1rem 0;

  // Animation/Visibility
  opacity: ${({ open }) => (open ? 1 : 0)};
  transform: ${({ open }) => (open ? "translateY(0)" : "translateY(-10px)")};
  visibility: ${({ open }) => (open ? "visible" : "hidden")};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease,
    visibility 0.3s ease;
  z-index: 0; // Below HeaderLayout (1000)

  @media (min-width: 993px) {
    // Hide on desktop
    display: none;
  }

  // Force link color within mobile menu
  ${NavLink} {
    color: ${({ theme }) => theme.primary || "#333"}; // Always use default text color
    text-shadow: 0 0 3px rgba(41, 41, 41, 0.2); // Optional glow
    font-size: 1.3rem;
    padding: 0.8rem 2rem; // Adjust padding for mobile links
    display: block;
    width: 100%;
    text-align: left;

    &:hover,
    &:focus {
      color: ${({ theme }) => theme.primary || "#ff5349"}; // Keep hover color consistent
    }
  }
`
